import { useEffect, useRef, useState } from 'react';

import type React from 'react';
import { Form, Spinner } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';
import { Controller, type FieldError, useFormContext } from 'react-hook-form';

import { getComponentFormError } from '../../../utils';

import './style.scss';

const MAX_FILE_SIZE = 10097152;

export interface FileModel {
  id?: string;
  name?: string;
}

export interface FileUploadResult {
  fieldValue: unknown;
  fileModel: FileModel;
}

export interface FileTexts {
  download: string;
  search: string;
  noFileSelected: string;
  fileUploadMaxSize: string;
  fileRequirements?: string;
  uploading?: string;
  uploaded?: string;
  uploadError?: string;
  selectFile?: string;
  replaceFile?: string;
  removeFile?: string;
  removing?: string;
  removeError?: string;
}

export interface FileProps {
  label: string;
  name: string;
  texts: FileTexts;
  uploadFile?: (file: File, name?: string) => Promise<FileUploadResult>;
  downloadFile?: (fileId: string) => Promise<void>;
  removeFile?: (fileId: string) => Promise<void>;
  onUploadSuccess?: () => void;
  onUploadError?: (errorMessage: string) => void;
  onRemoveError?: (errorMessage: string) => void;
  disabled?: boolean;
  defaultValue?: FileModel;
  readonly?: boolean;
  canDownload?: boolean;
  accept?: string;
  customError?: FieldError;
  noUpload?: boolean;
  hideErrorAfterSuccess?: boolean;
  required?: boolean;
}

function FileField(props: FileProps) {
  const {
    label,
    name,
    texts,
    uploadFile,
    downloadFile,
    removeFile,
    onUploadSuccess,
    onUploadError,
    onRemoveError,
    disabled,
    defaultValue,
    readonly,
    accept,
    customError,
    noUpload,
    hideErrorAfterSuccess,
    canDownload,
    required
  } = props;
  const {
    formState: { errors },
    setValue,
    control
  } = useFormContext();

  const [state, setState] = useState<{
    fileModel: FileModel | undefined;
    file: File | undefined;
    uploadedInSession: boolean;
    status: '' | 'loading' | 'success' | 'error' | 'deleting';
    errorMessage?: string;
  }>({
    fileModel: defaultValue ?? undefined,
    file: undefined,
    uploadedInSession: false,
    status: ''
  });
  const { file, fileModel, status } = state;
  const inputRef = useRef<HTMLInputElement>(null);
  const selectButtonRef = useRef<HTMLButtonElement>(null);
  const inputId = `${name}-input`;
  const errorId = `${name}-error`;

  const error = getComponentFormError(name, errors) ?? customError;
  const fileType = fileModel?.name?.toLowerCase().endsWith('.pdf') ? 'PDF' : undefined;
  const fileSizeInMb =
    file && Number.isFinite(file.size) && file.size > 0 ? file.size / 1024 / 1024 : undefined;
  const fileSize =
    fileSizeInMb && Number(fileSizeInMb.toFixed(1)) > 0
      ? `${fileSizeInMb.toFixed(1)} MB`
      : undefined;
  const hasFileMetadata = Boolean(fileType || fileSize);
  const canRemove =
    !readonly && (!fileModel?.id || (state.uploadedInSession && Boolean(removeFile)));
  const canShowDownload = Boolean(fileModel?.id && downloadFile && canDownload !== false);

  useEffect(() => {
    setState((current) => ({
      ...current,
      fileModel: defaultValue ?? undefined,
      file: undefined,
      uploadedInSession: false,
      status: '',
      errorMessage: undefined
    }));
  }, [defaultValue]);

  useEffect(() => {
    if (!readonly && file && fileModel && status === '') {
      const upload = async () => {
        if (file.size >= MAX_FILE_SIZE) {
          setState((current) => ({
            ...current,
            status: 'error',
            errorMessage: texts.fileUploadMaxSize
          }));
          onUploadError?.(texts.fileUploadMaxSize);
          return;
        }

        try {
          if (noUpload) {
            setValue(name, file);
            setState((current) => ({
              ...current,
              fileModel: { name: fileModel.name },
              status: 'success'
            }));
            onUploadSuccess?.();
            return;
          }

          if (!uploadFile) {
            throw new Error('Missing uploadFile adapter');
          }

          setState((current) => ({ ...current, status: 'loading' }));
          const result = await uploadFile(file, fileModel.name);

          if (!result.fileModel.id) {
            setState((current) => ({
              ...current,
              fileModel: result.fileModel,
              status: 'error',
              errorMessage: texts.uploadError
            }));
            onUploadError?.(texts.uploadError ?? '');
            return;
          }

          setValue(name, result.fieldValue);
          setState((current) => ({
            ...current,
            fileModel: result.fileModel,
            uploadedInSession: true,
            status: 'success'
          }));
          onUploadSuccess?.();
        } catch {
          setState((current) => ({
            ...current,
            status: 'error',
            errorMessage: texts.uploadError
          }));
        }
      };

      upload();
    }
  }, [
    file,
    fileModel,
    name,
    noUpload,
    onUploadError,
    onUploadSuccess,
    readonly,
    setValue,
    status,
    texts.fileUploadMaxSize,
    uploadFile
  ]);

  const fileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      return;
    }

    setState((current) => ({
      ...current,
      fileModel: { name: selectedFile.name },
      file: selectedFile,
      uploadedInSession: false,
      status: '',
      errorMessage: undefined
    }));
    event.target.value = '';
  };

  const download = async () => {
    if (!fileModel?.id || !downloadFile) {
      return;
    }

    await downloadFile(fileModel.id);
  };

  const remove = async () => {
    const remoteId = fileModel?.id;
    if (remoteId && !state.uploadedInSession) {
      return;
    }

    try {
      if (remoteId) {
        if (!removeFile) {
          throw new Error('Missing removeFile adapter');
        }

        setState((current) => ({ ...current, status: 'deleting', errorMessage: undefined }));
        await removeFile(remoteId);
      }

      setValue(name, undefined);
      setState({ fileModel: undefined, file: undefined, uploadedInSession: false, status: '' });
      requestAnimationFrame(() => selectButtonRef.current?.focus());
    } catch {
      const errorMessage = texts.removeError ?? texts.uploadError ?? '';
      setState((current) => ({ ...current, status: 'error', errorMessage }));
      onRemoveError?.(errorMessage);
    }
  };

  return (
    <Form.Group>
      <div className="dec-file-field">
        <div className="dec-file-field__label">
          <Form.Label className="mb-0" htmlFor={inputId}>
            {label}
            {required && (
              <span className="dec-file-field__required" aria-hidden="true">
                *
              </span>
            )}
          </Form.Label>
          {texts.fileRequirements && (
            <span className="dec-file-field__hint">{texts.fileRequirements}</span>
          )}
        </div>
        <Controller
          defaultValue={fileModel}
          name={name}
          render={() => {
            const isBusy = status === 'loading' || status === 'deleting';
            const actionDisabled = disabled || isBusy;

            return (
              <>
                {!readonly && (
                  <input
                    ref={inputRef}
                    type="file"
                    className="visually-hidden"
                    id={inputId}
                    accept={accept}
                    onChange={fileSelected}
                    disabled={actionDisabled}
                    tabIndex={-1}
                    aria-describedby={error ? errorId : undefined}
                    aria-required={required}
                  />
                )}
                {!fileModel?.name &&
                  (readonly ? (
                    <div className="dec-file-field__empty dec-file-field__empty--readonly">
                      {texts.noFileSelected}
                    </div>
                  ) : (
                    <button
                      ref={selectButtonRef}
                      type="button"
                      className="dec-file-field__empty"
                      onClick={() => inputRef.current?.click()}
                      disabled={actionDisabled}
                      aria-controls={inputId}>
                      {isBusy ? (
                        <Spinner animation="border" size="sm" aria-hidden="true" />
                      ) : (
                        <Icon.Upload aria-hidden="true" />
                      )}
                      <span>{isBusy ? texts.uploading : (texts.selectFile ?? texts.search)}</span>
                    </button>
                  ))}
                {fileModel?.name && (
                  <div className="dec-file-field__shell">
                    <div className="dec-file-field__identity">
                      <div className="dec-file-field__document-icon" aria-hidden="true">
                        {fileType ? <Icon.FileEarmarkPdf /> : <Icon.FileEarmark />}
                      </div>
                      <div className="dec-file-field__details">
                        <span className="dec-file-field__name" title={fileModel.name}>
                          {fileModel.name}
                        </span>
                        <span className="dec-file-field__metadata">
                          {fileType && <span>{fileType}</span>}
                          {fileType && fileSize && <span aria-hidden="true">&middot;</span>}
                          {fileSize && <span>{fileSize}</span>}
                          {(status === 'success' || fileModel.id) && !isBusy && (
                            <>
                              <>{hasFileMetadata && <span aria-hidden="true">&middot;</span>}</>
                              <span className="dec-file-field__success">
                                <Icon.Check2 aria-hidden="true" />
                                {texts.uploaded}
                              </span>
                            </>
                          )}
                          {status === 'loading' && (
                            <span className="dec-file-field__progress" role="status">
                              <Spinner animation="border" size="sm" aria-hidden="true" />
                              {texts.uploading}
                            </span>
                          )}
                          {status === 'deleting' && (
                            <span className="dec-file-field__progress" role="status">
                              <Spinner animation="border" size="sm" aria-hidden="true" />
                              {texts.removing}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="dec-file-field__actions">
                      {canShowDownload && (
                        <button
                          onClick={download}
                          className="dec-file-field__action"
                          type="button"
                          disabled={actionDisabled}
                          aria-label={texts.download}
                          title={texts.download}>
                          <Icon.Download aria-hidden="true" />
                        </button>
                      )}
                      {!readonly && (
                        <button
                          ref={selectButtonRef}
                          type="button"
                          className="dec-file-field__action"
                          onClick={() => inputRef.current?.click()}
                          disabled={actionDisabled}
                          aria-label={texts.replaceFile ?? texts.search}
                          title={texts.replaceFile ?? texts.search}
                          aria-controls={inputId}>
                          <Icon.ArrowRepeat aria-hidden="true" />
                        </button>
                      )}
                      {canRemove && (
                        <button
                          type="button"
                          className="dec-file-field__action dec-file-field__action--danger"
                          onClick={remove}
                          disabled={actionDisabled}
                          aria-label={texts.removeFile ?? ''}
                          title={texts.removeFile}>
                          <Icon.Trash3 aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {status === 'error' && (
                  <div className="dec-file-field__error" role="alert">
                    <Icon.X aria-hidden="true" />
                    {state.errorMessage}
                  </div>
                )}
              </>
            );
          }}
          control={control}
        />
      </div>

      <div id={errorId} className="invalid-feedback d-block">
        {hideErrorAfterSuccess && status === 'success' ? '' : (error?.message ?? '')}
      </div>
    </Form.Group>
  );
}

export default FileField;
